import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { Customer } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

interface PaginatedCustomerResponse {
  data: Customer[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetCustomer = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchCustomers = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'name',
    order: 'ASC' | 'DESC' = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedCustomerResponse>(
        apiUrls.CUSTOMER_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setCustomers(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setCustomers([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteCustomer = async (customerId: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_CUSTOMER,
        {
          params: { id: customerId },
        }
      );

      showSnackbar(response.data.message, 'success');
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return {
    loading,
    customers,
    totalItems,
    totalPages,
    currentPage,
    fetchCustomers,
    deleteCustomer,
  };
};

// Fetch a basic list of customers (no pagination/params)
export const getCustomerListBasic = async () => {
  const response = await axiosTokenInstance.get('/customer/list-basic');
  return response.data.data;
};

// Fetch addresses for a specific customer by ID
export const getCustomerAddresses = async (customerId: string) => {
  const response = await axiosTokenInstance.get(
    `/customer_address/${customerId}`
  );
  return response.data;
};

export const getBasicCustomers = async () => {
  const response = await axiosTokenInstance.get(
    '/customer/customer-list-basic'
  );
  return response.data.data;
};
